import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Show, SignInButton, SignUpButton, UserButton, useAuth, useUser } from '@clerk/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getStoredAuth, storeAuth, type AppRole } from '@/lib/auth';
import { api } from '@/lib/api-client';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Flame, LogIn, ShieldCheck } from 'lucide-react';

type ProtectedRouteProps = {
  requireRole: AppRole;
  children: React.ReactNode;
};

/**
 * HIGH-5 fix: Add server-side session validation via /api/me.
 * The client-side localStorage check is still a fast first-pass guard,
 * but we also verify the token server-side before rendering protected content.
 * Cross-tab logout is handled via a storage event listener.
 */
export function ProtectedRoute({ requireRole, children }: ProtectedRouteProps) {
  const { getToken } = useAuth();
  const { isLoaded, isSignedIn } = useUser();
  const [auth, setAuth] = useState(getStoredAuth);
  const { token, role } = auth;
  const hasLocalAuth = Boolean(token && role);
  const usesClerkAdmin = requireRole === 'admin' && isLoaded && isSignedIn;

  useEffect(() => {
    const onStorage = () => setAuth(getStoredAuth());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const { data: clerkAdminToken, isLoading: isClerkTokenLoading } = useQuery({
    queryKey: ['clerk-admin-token', usesClerkAdmin],
    queryFn: async () => await getToken(),
    enabled: usesClerkAdmin,
    retry: false,
    staleTime: 60 * 1000,
  });

  const sessionToken = usesClerkAdmin ? (clerkAdminToken ?? '') : token;
  const canValidateSession = usesClerkAdmin ? Boolean(clerkAdminToken) : hasLocalAuth;

  const { data: session, isLoading, isError } = useQuery({
    queryKey: ['auth-me', sessionToken],
    queryFn: () =>
      api<{ userId: string; role: AppRole }>('/api/me', {
        headers: { Authorization: `Bearer ${sessionToken}` },
      }),
    enabled: canValidateSession,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  if (requireRole === 'user') {
    if (!isLoaded) {
      return (
        <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">
          Loading sign-in state…
        </div>
      );
    }

    if (!isSignedIn) {
      return <ClerkLoginGate role="user" />;
    }

    return <>{children}</>;
  }

  if (usesClerkAdmin && isClerkTokenLoading) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">
        Loading admin session…
      </div>
    );
  }

  // Admin route: Clerk is the primary path. Local auth still works for
  // operators with a username/password (legacy `/api/login`) once they're
  // signed in elsewhere, but the UI prompt is now Clerk only.
  if (!usesClerkAdmin && !hasLocalAuth) {
    if (!isLoaded) {
      return (
        <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">
          Loading sign-in state…
        </div>
      );
    }
    return <ClerkLoginGate role="admin" />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">
        Verifying session…
      </div>
    );
  }

  // Server rejected token or session expired → fall back to the Clerk gate
  // (admin) or the legacy gate (user) so they can re-authenticate.
  if (isError || !session) {
    return requireRole === 'admin'
      ? <ClerkLoginGate role="admin" />
      : <LoginGate requireRole={requireRole} onAuthenticated={() => setAuth(getStoredAuth())} />;
  }

  // Role mismatch — admin-only route reached by a non-admin session
  if (session.role !== requireRole) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function ClerkLoginGate({ role = 'user' }: { role?: AppRole }) {
  const isAdmin = role === 'admin';
  return (
    <AppLayout>
      <main className="min-h-[calc(100vh-3.5rem)] grid place-items-center px-4 py-10">
        <Card className="w-full max-w-sm border-primary/15 shadow-lg">
          <CardHeader className="space-y-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              {isAdmin ? <ShieldCheck className="h-5 w-5" /> : <Flame className="h-5 w-5" />}
            </div>
            <div>
              <CardTitle className="text-2xl font-display">{isAdmin ? 'Admin sign in' : 'Sign in'}</CardTitle>
              <CardDescription>
                {isAdmin
                  ? 'Sign in with Clerk to access the admin dashboard. Only authorized accounts can continue.'
                  : 'Sign in to access your Pooram App dashboard.'}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <Show when="signed-out">
              <SignInButton mode="modal">
                <Button className="w-full font-bold">
                  <LogIn className="mr-2 h-4 w-4" />
                  Sign in with Clerk
                </Button>
              </SignInButton>
              {!isAdmin && (
                <SignUpButton mode="modal">
                  <Button variant="outline" className="w-full font-bold">
                    Sign up
                  </Button>
                </SignUpButton>
              )}
            </Show>
            <Show when="signed-in">
              <div className="flex items-center justify-between rounded-xl border p-3">
                <span className="text-sm font-semibold">
                  {isAdmin ? 'Signed in — verifying admin access…' : 'Signed in'}
                </span>
                <UserButton />
              </div>
            </Show>
          </CardContent>
        </Card>
      </main>
    </AppLayout>
  );
}

type LoginResponse = {
  token: string;
  user: {
    username: string;
    role: AppRole;
  };
};

function LoginGate({ requireRole, onAuthenticated }: { requireRole: AppRole; onAuthenticated: () => void }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState(requireRole === 'admin' ? 'admin' : '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const loginMutation = useMutation({
    mutationFn: (creds: { username: string; password: string }) =>
      api<LoginResponse>('/api/login', {
        method: 'POST',
        body: JSON.stringify(creds),
      }),
    onSuccess: (data) => {
      storeAuth(data.token, data.user.role);
      onAuthenticated();
      setError('');
      navigate(data.user.role === 'admin' ? '/admin' : '/dashboard', { replace: true });
    },
    onError: (err: Error) => {
      setError(err.message || 'Login failed. Check credentials.');
    },
  });

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    if (!username.trim() || !password.trim()) {
      setError('Enter username and password.');
      return;
    }
    loginMutation.mutate({ username: username.trim(), password });
  };

  return (
    <AppLayout>
      <main className="min-h-[calc(100vh-3.5rem)] grid place-items-center px-4 py-10">
        <Card className="w-full max-w-sm border-primary/15 shadow-lg">
          <CardHeader className="space-y-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              {requireRole === 'admin' ? <ShieldCheck className="h-5 w-5" /> : <Flame className="h-5 w-5" />}
            </div>
            <div>
              <CardTitle className="text-2xl font-display">Admin Login</CardTitle>
              <CardDescription>Sign in to access the protected dashboard.</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form className="space-y-3" onSubmit={submit}>
              <Input
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="Username"
                autoComplete="username"
                disabled={loginMutation.isPending}
              />
              <Input
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Password"
                type="password"
                autoComplete="current-password"
                disabled={loginMutation.isPending}
              />
              {error ? <p className="text-sm font-semibold text-destructive">{error}</p> : null}
              <Button type="submit" className="w-full font-bold" disabled={loginMutation.isPending}>
                <LogIn className="mr-2 h-4 w-4" />
                {loginMutation.isPending ? 'Signing in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </AppLayout>
  );
}
